import React, { useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import defaultLogo from "../../assets/Logo.png";

const BRAND_BLUE = "#2563EB";
const BRAND_PURPLE = "#4F46E5";
const DARK = "#0B1220";
const TEXT = "#172033";
const MUTED = "#64748B";

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;

  text-decoration: none;
  color: ${DARK};
`;

const BrandMark = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  color: white;
  font-size: 16px;
  font-weight: 800;

  box-shadow: 0 7px 18px rgba(37, 99, 235, 0.2);
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1;

  strong {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.4px;
    color: ${DARK};
  }

  span {
    margin-top: 4px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.6px;
    color: ${BRAND_BLUE};
  }
`;

/* =========================================================
   PAGE
========================================================= */

const Home = ({ appData }) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const logo = appData?.data?.logo || defaultLogo;

  return (
    <Page>
      {/* =====================================================
          HEADER
      ===================================================== */}
      <Header>
        <HeaderInner>
          {/* <LogoWrap>
            <Logo
              src={logo}
              alt="Recharge Junction Logo"
              onError={(e) => {
                e.currentTarget.src = defaultLogo;
              }}
            />
          </LogoWrap> */}
          <Brand to="/">
            <BrandMark>API</BrandMark>

            <BrandText>
              <strong>API EXCHANGE</strong>
              <span>POWERING BUSINESS</span>
            </BrandText>
          </Brand>

          <Navigation>
            {/* <NavLink to="/services">Services</NavLink> */}
            <NavLink to="/privacy-policy">Privacy Policy</NavLink>
            <NavLink to="/terms-and-conditions">
              Terms & Conditions
            </NavLink>
            <NavLink to="/refund-policy">
              Refund & Cancellation
            </NavLink>
            <NavLink to="/contact">Contact Us</NavLink>
          </Navigation>

          <HeaderActions>
            <LoginButton to="/login">
              Login
              <span>→</span>
            </LoginButton>

            <StartButton to="/register">
              Start Free
              <span>→</span>
            </StartButton>
          </HeaderActions>
        </HeaderInner>
      </Header>

      {/* =====================================================
          HERO
      ===================================================== */}
      <Hero>
        <HeroInner>
          <HeroContent>
            <Eyebrow>
              <EyebrowDot />
              India’s Smart Recharge Platform
            </Eyebrow>

            <HeroTitle>
              Reliable Recharge APIs
              <br />
              Built for <HeroHighlight>Growing Businesses</HeroHighlight>
            </HeroTitle>

            <HeroDescription>
              Reliable recharge APIs for Mobile, DTH, Data & digital services
              — built for speed, scale, and high success rates.
            </HeroDescription>

            <HeroActions>
              <PrimaryButton to="/register">
                Register
                <span>→</span>
              </PrimaryButton>

              <SecondaryButton to="/login">
                Login
                <span>→</span>
              </SecondaryButton>

              <TetriaryButton to="/contact">
                Support
                <span>→</span>
              </TetriaryButton>
            </HeroActions>
          </HeroContent>

          <HeroVisual>
            <VisualCard>
              <VisualTop>
                <VisualLabel>API EXCHANGE</VisualLabel>

                <LiveStatus>
                  <StatusDot />
                  LIVE
                </LiveStatus>
              </VisualTop>

              <VisualTitle>
                Built for
                <br />
                <strong>Reliable Transactions</strong>
              </VisualTitle>

              <VisualLines>
                <VisualLine>
                  <LineIcon>01</LineIcon>
                  <LineContent>
                    <LineName>Recharge API</LineName>
                    <LineBar>
                      <LineProgress />
                    </LineBar>
                  </LineContent>
                </VisualLine>

                <VisualLine>
                  <LineIcon>02</LineIcon>
                  <LineContent>
                    <LineName>B2B Exchange</LineName>
                    <LineBar>
                      <LineProgress />
                    </LineBar>
                  </LineContent>
                </VisualLine>

                <VisualLine>
                  <LineIcon>03</LineIcon>
                  <LineContent>
                    <LineName>Real-Time Reports</LineName>
                    <LineBar>
                      <LineProgress />
                    </LineBar>
                  </LineContent>
                </VisualLine>
              </VisualLines>

              <VisualBottom>
                <VisualBottomText>
                  Secure infrastructure
                </VisualBottomText>

                <VisualArrow>↗</VisualArrow>
              </VisualBottom>
            </VisualCard>
          </HeroVisual>
        </HeroInner>
      </Hero>

      {/* =====================================================
          FEATURE STRIP
      ===================================================== */}
      <FeatureSection>
        <FeatureGrid>
          <FeatureItem>
            <FeatureIcon>⚡</FeatureIcon>

            <FeatureText>
              <FeatureTitle>High Success Rates</FeatureTitle>
            </FeatureText>
          </FeatureItem>

          <FeatureItem>
            <FeatureIcon>🔐</FeatureIcon>

            <FeatureText>
              <FeatureTitle>Secure Transactions</FeatureTitle>
            </FeatureText>
          </FeatureItem>

          <FeatureItem>
            <FeatureIcon>📊</FeatureIcon>

            <FeatureText>
              <FeatureTitle>Real-Time Reports</FeatureTitle>
            </FeatureText>
          </FeatureItem>

          <FeatureItem>
            <FeatureIcon>🧩</FeatureIcon>

            <FeatureText>
              <FeatureTitle>Powerful APIs</FeatureTitle>
            </FeatureText>
          </FeatureItem>
        </FeatureGrid>
      </FeatureSection>

      {/* =====================================================
          ABOUT
      ===================================================== */}
      <Section>
        <SectionHeader>
          <SectionEyebrow>WHO WE ARE</SectionEyebrow>

          <SectionTitle>
            Technology that keeps your
            <br />
            <SectionTitleAccent>recharge business moving.</SectionTitleAccent>
          </SectionTitle>
        </SectionHeader>

        <AboutGrid>
          <AboutNumber>01</AboutNumber>

          <AboutContent>
            <AboutHeading>About Us</AboutHeading>

            <AboutText>
              At <strong>API Exchange</strong>, we empower businesses in the
              telecom and digital services sector through a secure,
              transparent, and efficient B2B platform for trading recharge
              balances.
            </AboutText>

            <AboutText>
              Whether you're a reseller, distributor, or service provider, our
              exchange enables seamless transactions of prepaid balances across
              multiple networks and operators.
            </AboutText>

            <AboutText>
              Built with cutting-edge technology and a focus on reliability,
              <strong> API Exchange</strong> ensures real-time operations,
              trusted partnerships, and scalable growth for all participants.
              Join us to streamline your recharge ecosystem and unlock new
              revenue opportunities.
            </AboutText>
          </AboutContent>
        </AboutGrid>
      </Section>

      {/* =====================================================
          SERVICES
      ===================================================== */}
      <ServicesSection>
        <SectionHeader>
          <SectionEyebrow>OUR SERVICES</SectionEyebrow>

          <SectionTitle>
            Everything you need to
            <br />
            <SectionTitleAccent>scale your recharge business.</SectionTitleAccent>
          </SectionTitle>
        </SectionHeader>

        <ServicesGrid>
          <ServiceCard>
            <ServiceIcon>📱</ServiceIcon>

            <ServiceContent>
              <ServiceNumber>01</ServiceNumber>
              <ServiceTitle>Recharge Services</ServiceTitle>
              <ServiceDescription>
                Fast and reliable Mobile, DTH & Data recharge services.
              </ServiceDescription>
            </ServiceContent>
          </ServiceCard>

          <ServiceCard>
            <ServiceIcon>🔌</ServiceIcon>

            <ServiceContent>
              <ServiceNumber>02</ServiceNumber>
              <ServiceTitle>Powerful Recharge APIs</ServiceTitle>
              <ServiceDescription>
                Easy-to-integrate APIs built for automation and scalability.
              </ServiceDescription>
            </ServiceContent>
          </ServiceCard>

          <ServiceCard>
            <ServiceIcon>💼</ServiceIcon>

            <ServiceContent>
              <ServiceNumber>03</ServiceNumber>
              <ServiceTitle>B2B Recharge Exchange</ServiceTitle>
              <ServiceDescription>
                Seamless balance trading between resellers, distributors & providers.
              </ServiceDescription>
            </ServiceContent>
          </ServiceCard>

          <ServiceCard>
            <ServiceIcon>📈</ServiceIcon>

            <ServiceContent>
              <ServiceNumber>04</ServiceNumber>
              <ServiceTitle>Real-Time Reports</ServiceTitle>
              <ServiceDescription>
                Track transactions, balances, settlements and business performance.
              </ServiceDescription>
            </ServiceContent>
          </ServiceCard>
        </ServicesGrid>
      </ServicesSection>

      {/* =====================================================
          CTA
      ===================================================== */}
      {/* <CTASection>
        <CTAInner>
          <CTAContent>
            <CTAOverline>READY TO Start Free?</CTAOverline>

            <CTATitle>Start your Journey with us</CTATitle>

            <CTADescription>
              Quick onboarding • Reliable APIs • Dedicated support
            </CTADescription>
          </CTAContent>

          <CTAButton to="/register">
            Start Free
            <span>→</span>
          </CTAButton>
        </CTAInner>
      </CTASection> */}

      {/* =====================================================
          FOOTER
          Links have been moved to the top navigation.
      ===================================================== */}
      <Footer>
        <FooterInner>
          <FooterBrand>
            {/* <FooterLogo
              src={logo}
              alt="Recharge Junction Logo"
              onError={(e) => {
                e.currentTarget.src = defaultLogo;
              }}
            /> */}

            <FooterBrandText>
              Reliable technology for growing recharge businesses.
            </FooterBrandText>
          </FooterBrand>

          <Copyright>
            © {"ERech ReSellers Private Limited. All Rights Reserved"}
          </Copyright>
        </FooterInner>
      </Footer>
    </Page >
  );
};

/* =========================================================
   STYLED COMPONENTS
========================================================= */

const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
  color: #111827;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
`;

const Header = styled.header`
  width: 100%;
  height: 76px;
  background: #ffffff;
  border-bottom: 1px solid #e8e8ef;
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: 1380px;
  height: 100%;
  margin: 0 auto;
  padding: 0 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;

  @media (max-width: 900px) {
    padding: 0 24px;
  }
`;

const LogoWrap = styled.div`
  width: 175px;
  height: 52px;

  display: flex;
  align-items: center;
  justify-content: flex-start;

  flex-shrink: 0;
`;

const Logo = styled.img`
  width: 150px;
  max-width: 100%;
  max-height: 48px;

  display: block;
  object-fit: contain;

  opacity: 1;
  visibility: visible;
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 27px;

  flex: 1;

  @media (max-width: 1100px) {
    gap: 17px;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #252b3a;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  transition: color 0.2s ease;

  &:hover {
    color: #150080;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  flex-shrink: 0;
`;

const LoginLink = styled(Link)`
  color: #202635;
  text-decoration: none;

  font-size: 14px;
  font-weight: 700;

  padding: 10px 12px;

  &:hover {
    color: #150080;
  }
`;

const StartButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  color: #ffffff;
  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  text-decoration: none;

  padding: 11px 18px;
  border-radius: 6px;

  font-size: 13px;
  font-weight: 700;

  transition:
    background 0.2s ease,
    transform 0.2s ease;

  span {
    font-size: 17px;
    line-height: 1;
  }

  &:hover {
    background: #10005e;
    transform: translateY(-1px);
  }
`;

const LoginButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  color: #ffffff;

  background: linear-gradient(
    135deg,
    #59047d 0%,
    #340548 55%,
    #59047d 100%
  );

  text-decoration: none;

  padding: 11px 18px;
  border-radius: 6px;

  font-size: 13px;
  font-weight: 700;

  box-shadow: 0 7px 18px rgba(21, 0, 128, 0.2);

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    filter 0.2s ease;

  span {
    font-size: 17px;
    line-height: 1;
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.08);
    box-shadow: 0 10px 24px rgba(21, 0, 128, 0.28);
  }
`;
/* =========================================================
   HERO
========================================================= */

const Hero = styled.section`
  width: 100%;
  background:
    radial-gradient(
      circle at 85% 30%,
      rgba(21, 0, 128, 0.07),
      transparent 30%
    ),
    #9489fa;
`;

const HeroInner = styled.div`
  max-width: 1380px;
  min-height: 590px;

  margin: 0 auto;
  padding: 80px 42px;

  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(380px, 0.75fr);
  align-items: center;
  gap: 80px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 50px;
    padding: 65px 24px;
  }
`;

const HeroContent = styled.div`
  max-width: 720px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 9px;

  padding: 8px 12px;

  background: #f5f3ff;
  border: 1px solid #e8e2ff;
  border-radius: 5px;

  color: #150080;

  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.7px;
  text-transform: uppercase;
`;

const EyebrowDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #150080;
  display: inline-block;
`;

const HeroTitle = styled.h1`
  margin: 25px 0 20px;

  color: #111827;

  font-size: clamp(42px, 5vw, 68px);
  line-height: 1.05;
  letter-spacing: -2.5px;
  font-weight: 800;

  @media (max-width: 600px) {
    font-size: 42px;
    letter-spacing: -1.5px;
  }
`;

const HeroHighlight = styled.span`
  color: #150080;
`;

const HeroDescription = styled.p`
  max-width: 650px;

  margin: 0;

  color: #00050e;

  font-size: 17px;
  line-height: 1.75;
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  margin-top: 32px;
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  width: 120px;
  box-sizing: border-box;

  padding: 14px 22px;
  border-radius: 6px;

  background: #150080;
  color: #ffffff;

  text-decoration: none;
  font-size: 14px;
  font-weight: 700;

  span {
    font-size: 18px;
  }

  &:hover {
    background: #10005e;
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 120px;
  box-sizing: border-box;

  padding: 14px 22px;
  border: 1px solid #d9dce5;
  border-radius: 6px;

  background: #59047d;
  color: #ffffff;

  text-decoration: none;
  font-size: 14px;
  font-weight: 700;

  &:hover {
    border-color: #150080;
    color: #150080;
  }
`;

const TetriaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 120px;
  box-sizing: border-box;

  padding: 14px 22px;
  border: 1px solid #d9dce5;
  border-radius: 6px;

  background: #ffffff;
  color: #202635;

  text-decoration: none;
  font-size: 14px;
  font-weight: 700;

  &:hover {
    border-color: #150080;
    color: #150080;
  }
`;

const SupportLink = styled(Link)`
  color: #150080;
  text-decoration: none;

  font-size: 14px;
  font-weight: 700;

  padding: 14px 8px;

  &:hover {
    text-decoration: underline;
  }
`;

/* =========================================================
   HERO VISUAL
========================================================= */

const HeroVisual = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VisualCard = styled.div`
  width: 100%;
  max-width: 470px;

  padding: 32px;

  background: #111827;
  border-radius: 12px;

  box-shadow: 0 25px 70px rgba(17, 24, 39, 0.18);

  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;

    width: 230px;
    height: 230px;

    right: -80px;
    top: -80px;

    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
  }

  &::after {
    content: "";
    position: absolute;

    width: 320px;
    height: 320px;

    right: -130px;
    top: -130px;

    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 50%;
  }
`;

const VisualTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  position: relative;
  z-index: 2;
`;

const VisualLabel = styled.span`
  color: #ffffff;

  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.5px;
`;

const LiveStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;

  color: #c7cad3;

  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1px;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;

  border-radius: 50%;
  background: #73d13d;
`;

const VisualTitle = styled.div`
  position: relative;
  z-index: 2;

  margin-top: 65px;

  color: #9fa6b6;

  font-size: 14px;
  line-height: 1.5;

  strong {
    color: #ffffff;
    font-size: 30px;
    line-height: 1.15;
    font-weight: 700;
  }
`;

const VisualLines = styled.div`
  margin-top: 42px;

  display: flex;
  flex-direction: column;
  gap: 22px;

  position: relative;
  z-index: 2;
`;

const VisualLine = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const LineIcon = styled.div`
  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 5px;

  color: #c4b8ff;

  font-size: 10px;
  font-weight: 800;
`;

const LineContent = styled.div`
  flex: 1;
`;

const LineName = styled.div`
  margin-bottom: 7px;

  color: #dfe2e8;

  font-size: 12px;
  font-weight: 600;
`;

const LineBar = styled.div`
  width: 100%;
  height: 4px;

  border-radius: 10px;

  background: rgba(255, 255, 255, 0.1);

  overflow: hidden;
`;

const LineProgress = styled.div`
  width: 82%;
  height: 100%;

  border-radius: inherit;

  background: #8170ff;
`;

const VisualBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-top: 42px;
  padding-top: 20px;

  border-top: 1px solid rgba(255, 255, 255, 0.1);

  position: relative;
  z-index: 2;
`;

const VisualBottomText = styled.span`
  color: #8991a2;
  font-size: 11px;
`;

const VisualArrow = styled.span`
  color: #ffffff;
  font-size: 20px;
`;

/* =========================================================
   FEATURE STRIP
========================================================= */

const FeatureSection = styled.section`
  width: 100%;

  border-top: 1px solid #d9e2ff;
  border-bottom: 1px solid #d9e2ff;

  background: linear-gradient(
    180deg,
    #f4f7ff 0%,
    #eef4ff 100%
  );
`;

const FeatureGrid = styled.div`
  max-width: 1380px;
  margin: 0 auto;

  display: grid;
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  min-height: 105px;

  padding: 25px 30px;

  display: flex;
  align-items: center;
  gap: 14px;

  border-right: 1px solid #e8e8ef;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 800px) {
    &:nth-child(2) {
      border-right: none;
    }

    &:nth-child(3),
    &:nth-child(4) {
      border-top: 1px solid #e8e8ef;
    }
  }
`;

const FeatureIcon = styled.div`
  font-size: 22px;
`;

const FeatureText = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.div`
  color: #202635;

  font-size: 13px;
  font-weight: 800;
`;

/* =========================================================
   GENERAL SECTION
========================================================= */

const Section = styled.section`
  max-width: 1180px;

  margin: 0 auto;
  padding: 110px 30px;

  @media (max-width: 700px) {
    padding: 75px 24px;
  }
`;

const ServiceCard = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  
  min-height: 155px;
  padding: 22px;

  background: #9489fa;
  border: 1px solid #e2e6ee;
  border-radius: 10px;

  overflow: hidden;

  transition:
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    left: 22px;
    bottom: 0;
    width: 32px;
    height: 2px;

    background: linear-gradient(90deg, #2563eb, #4f46e5);
    border-radius: 2px;

    transition: width 0.25s ease;
  }

  &:hover {
    border-color: #d2d8e5;
    transform: translateY(-3px);
    box-shadow: 0 14px 32px rgba(15, 23, 42, 0.07);

    &::after {
      width: 58px;
    }
  }
`;

const ServiceIcon = styled.div`
  flex: 0 0 46px;

  width: 46px;
  height: 46px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: 2px;

  border: 1px solid #e0e6f2;
  border-radius: 9px;

  background: linear-gradient(
    145deg,
    #f4f7ff 0%,
    #f5f3ff 100%
  );

  font-size: 20px;

  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;

  ${ServiceCard}:hover & {
    transform: translateY(-2px);
    border-color: #d2daf0;
    background: linear-gradient(
      145deg,
      #eef4ff 0%,
      #f1efff 100%
    );
  }
`;

const ServiceContent = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  padding-right: 25px;
`;

const ServiceNumber = styled.div`
  position: absolute;
  top: -1px;
  right: 0;

  color: #01060e;

  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
`;

const ServiceTitle = styled.h3`
  margin: 1px 0 8px;

  color: #151b28;

  font-size: 17px;
  line-height: 1.35;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const ServiceDescription = styled.p`
  max-width: 430px;
  margin: 0;

  color: #ebeef4;

  font-size: 14px;
  line-height: 1.65;
`;

const SectionHeader = styled.div`
  margin-bottom: 55px;
`;

const SectionEyebrow = styled.div`
  margin-bottom: 15px;

  color: #150080;

  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.5px;
`;

const SectionTitle = styled.h2`
  margin: 0;

  color: #000308;

  font-size: clamp(32px, 4vw, 48px);
  font-weight: 800px;
  line-height: 1.12;
  letter-spacing: -1.5px;
`;

const SectionTitleAccent = styled.span`
  color: #150080;
`;

/* =========================================================
   ABOUT
========================================================= */

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 60px;

  padding-top: 10px;

  border-top: 1px solid #e3e5eb;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }
`;

const AboutNumber = styled.div`
  padding-top: 28px;

  color: #150080;

  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1px;
`;

const AboutContent = styled.div`
  max-width: 850px;
  padding-top: 25px;
`;

const AboutHeading = styled.h3`
  margin: 0 0 25px;

  color: #111827;

  font-size: 28px;
  font-weight: 750;
`;

const AboutText = styled.p`
  margin: 0 0 20px;

  color: #5f6675;

  font-size: 16px;
  line-height: 1.8;

  strong {
    color: #202635;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

/* =========================================================
   SERVICES
========================================================= */

const ServicesSection = styled.section`
  max-width: 1180px;

  margin: 0 auto;
  padding: 40px 30px 110px;

  @media (max-width: 700px) {
    padding: 30px 24px 75px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;


const ServiceTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;


/* =========================================================
   CTA
========================================================= */

const CTASection = styled.section`
  max-width: 1180px;

  margin: 0 auto 100px;
  padding: 0 30px;

  @media (max-width: 700px) {
    padding: 0 24px;
    margin-bottom: 70px;
  }
`;

const CTAInner = styled.div`
  min-height: 220px;

  padding: 45px 50px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 35px;

  background: #150080;

  border-radius: 10px;

  position: relative;
  overflow: hidden;

  &::after {
    content: "";

    position: absolute;

    width: 300px;
    height: 300px;

    right: -130px;
    top: -150px;

    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 50%;
  }

  @media (max-width: 700px) {
    padding: 35px 28px;

    flex-direction: column;
    align-items: flex-start;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 2;
`;

const CTAOverline = styled.div`
  margin-bottom: 10px;

  color: #c9c2ff;

  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.5px;
`;

const CTATitle = styled.h2`
  margin: 0 0 10px;

  color: #ffffff;

  font-size: 32px;
  font-weight: 750;
`;

const CTADescription = styled.p`
  margin: 0;

  color: #d8d5ed;

  font-size: 14px;
`;

const CTAButton = styled(Link)`
  position: relative;
  z-index: 2;

  display: inline-flex;
  align-items: center;
  gap: 12px;

  padding: 14px 22px;

  background: #ffffff;
  color: #150080;

  border-radius: 6px;

  text-decoration: none;

  font-size: 14px;
  font-weight: 800;

  white-space: nowrap;

  span {
    font-size: 18px;
  }

  &:hover {
    background: #f4f2ff;
  }
`;

/* =========================================================
   FOOTER
========================================================= */

const Footer = styled.footer`
  width: 100%;

  background: #759ef9;

  color: #ffffff;
`;

const FooterInner = styled.div`
  max-width: 1380px;

  margin: 0 auto;

  min-height: 95px;

  padding: 25px 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 30px;

  @media (max-width: 700px) {
    padding: 25px 24px;

    flex-direction: column;
    align-items: flex-start;
  }
`;

const FooterBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const FooterLogo = styled.img`
  width: 120px;
  max-height: 40px;

  object-fit: contain;

  display: block;

  opacity: 1;
  visibility: visible;
`;

const FooterBrandText = styled.span`
  color: #010610;

  font-size: 12px;
`;

const Copyright = styled.div`
  color: #010610;

  font-size: 11px;

  text-align: right;

  @media (max-width: 700px) {
    text-align: left;
  }
`;

export default Home;